// components/PricingPage.jsx - 定价页面
import React, { useState } from 'react';
import { theme } from '../theme';
import { useAuth } from '../AuthContext';
import { getTrialInfo } from '../firebase';
import { AuthModal } from './AuthModal';

export function PricingPage({ onBack }) {
  const { user, userData } = useAuth();
  const trialInfo = getTrialInfo(userData);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(null);
  const [authModal, setAuthModal] = useState({ open: false, mode: 'signup' });

  const currentTier = userData?.tier || 'free';

  // 判断当前计划是否匹配（需要同时匹配 plan 类型和计费周期）
  const isCurrentPlan = (plan) => {
    if (plan === 'free') return currentTier === 'free';
    if (plan === 'pro') {
      if (billingCycle === 'monthly') return currentTier === 'pro_monthly';
      if (billingCycle === 'yearly') return currentTier === 'pro_yearly';
    }
    if (plan === 'proplus') {
      if (billingCycle === 'monthly') return currentTier === 'proplus_monthly';
      if (billingCycle === 'yearly') return currentTier === 'proplus_yearly';
    }
    return false;
  };

  // 判断用户是否是某个 plan 级别（不区分计费周期）
  const isPlanLevel = (plan) => {
    if (plan === 'free') return currentTier === 'free';
    if (plan === 'pro') return currentTier === 'pro_monthly' || currentTier === 'pro_yearly';
    if (plan === 'proplus') return currentTier === 'proplus_monthly' || currentTier === 'proplus_yearly';
    return false;
  };

  const plans = {
    monthly: {
      pro: { price: 7.99, originalPrice: 15.99 },
      proplus: { price: 14.99, originalPrice: 29.99 }
    },
    yearly: {
      pro: { price: 59.99, originalPrice: 119.99, monthly: 5.00 },
      proplus: { price: 99.99, originalPrice: 199.99, monthly: 8.33 }
    }
  };

  const handleCheckout = async (plan) => {
    if (!user) {
      setAuthModal({ open: true, mode: 'signup' });
      return;
    }
    setLoading(plan);
    try {
      const planKey = plan === 'pro' ? billingCycle : billingCycle + '_plus';
      const response = await fetch('https://insightstock-api-358482645476.us-west2.run.app/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planKey, user_id: user.uid })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      alert('Failed to start checkout.');
    }
    setLoading(null);
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    setLoading('manage');
    try {
      const response = await fetch('https://insightstock-api-358482645476.us-west2.run.app/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.uid })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      alert('Failed to open portal.');
    }
    setLoading(null);
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: '-apple-system, sans-serif', color: theme.text }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px' }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: '1px solid ' + theme.border,
            borderRadius: '8px',
            padding: '16px 28px',
            color: theme.textMuted,
            fontSize: '18px',
            cursor: 'pointer',
            marginBottom: '40px'
          }}
        >
          ← Back
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: 'Georgia, serif',
            fontSize: '48px',
            fontWeight: 400,
            color: theme.text,
            margin: '0 0 16px'
          }}>
            Choose Your Plan
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '22px', margin: '0 0 20px' }}>
            AI-powered stock analysis starts at $7.99/month
          </p>
          <div style={{
            display: 'inline-block',
            background: theme.teal,
            padding: '10px 24px',
            borderRadius: '20px'
          }}>
            <p style={{ color: '#0f172a', fontSize: '18px', fontWeight: 700, margin: 0 }}>
              Launch Special: 50% OFF all plans!
            </p>
          </div>
        </div>

        {/* Billing Toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '8px',
          marginBottom: '40px',
          background: theme.bgCard,
          padding: '8px',
          borderRadius: '12px',
          width: 'fit-content',
          margin: '0 auto 40px'
        }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              background: billingCycle === 'monthly' ? theme.teal : 'transparent',
              border: 'none',
              borderRadius: '8px',
              padding: '16px 36px',
              color: billingCycle === 'monthly' ? '#0f172a' : theme.textMuted,
              fontSize: '20px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            style={{
              background: billingCycle === 'yearly' ? theme.teal : 'transparent',
              border: 'none',
              borderRadius: '8px',
              padding: '16px 36px',
              color: billingCycle === 'yearly' ? '#0f172a' : theme.textMuted,
              fontSize: '20px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Yearly (Save 50%)
          </button>
        </div>

        {/* Pricing Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '60px' }}>

          {/* Free */}
          <div style={{
            background: theme.bgCard,
            border: isCurrentPlan('free') ? '2px solid ' + theme.teal : '1px solid ' + theme.border,
            borderRadius: '20px',
            padding: '36px',
            position: 'relative'
          }}>
            {isCurrentPlan('free') && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: theme.teal,
                color: '#0f172a',
                padding: '4px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700
              }}>
                CURRENT
              </div>
            )}
            <h3 style={{ fontFamily: 'Georgia, serif', color: theme.teal, margin: '0 0 8px', fontSize: '28px', fontWeight: 400 }}>Free</h3>
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontSize: '48px', fontWeight: 700, color: theme.text }}>$0</span>
            </div>
            <p style={{ color: theme.textDark, fontSize: '14px', margin: '0 0 24px' }}>Free forever · No credit card needed</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px' }}>
              {['Full access forever', 'Real-time stock price', 'Technical signals', 'AI quick summary', 'Daily AI Top Pick', 'Full AI Analysis'].map((item, i) => (
                <li key={i} style={{
                  color: theme.textMuted,
                  fontSize: '16px',
                  padding: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  borderBottom: '1px solid ' + theme.border
                }}>
                  <span style={{ color: theme.teal }}>✓</span> {item}
                </li>
              ))}
            </ul>
            {currentTier === 'free' ? (
              <button
                disabled
                style={{
                  width: '100%',
                  background: theme.border,
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.textDark,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'not-allowed'
                }}
              >
                Current Plan
              </button>
            ) : (
              <button
                onClick={handleManageSubscription}
                disabled={loading === 'manage'}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '2px solid ' + theme.border,
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.textMuted,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {loading === 'manage' ? 'Loading...' : 'Downgrade'}
              </button>
            )}
          </div>

          {/* Pro */}
          <div style={{
            background: theme.bgCard,
            border: isCurrentPlan('pro') ? '2px solid ' + theme.blue : '1px solid ' + theme.blue,
            borderRadius: '20px',
            padding: '36px',
            position: 'relative'
          }}>
            {isCurrentPlan('pro') && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: theme.blue,
                color: '#fff',
                padding: '4px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700
              }}>
                CURRENT
              </div>
            )}
            <h3 style={{ fontFamily: 'Georgia, serif', color: theme.blue, margin: '0 0 8px', fontSize: '28px', fontWeight: 400 }}>Pro</h3>
            <div style={{ marginBottom: '24px' }}>
              <span style={{ color: theme.textDark, fontSize: '18px', textDecoration: 'line-through', marginRight: '8px' }}>
                ${plans[billingCycle].pro.originalPrice}
              </span>
              <span style={{ fontSize: '48px', fontWeight: 700, color: theme.text }}>
                ${plans[billingCycle].pro.price}
              </span>
              <span style={{ color: theme.textMuted, fontSize: '18px' }}>
                /{billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
              {billingCycle === 'yearly' && (
                <p style={{ color: theme.green, fontSize: '14px', margin: '8px 0 0' }}>
                  ${plans.yearly.pro.monthly}/month
                </p>
              )}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px' }}>
              {['100 queries per day', 'Everything in Free', 'Deep AI news analysis', 'Financial charts & trends', 'Risk assessment', 'Sentiment analysis'].map((item, i) => (
                <li key={i} style={{
                  color: theme.textMuted,
                  fontSize: '16px',
                  padding: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  borderBottom: '1px solid ' + theme.border
                }}>
                  <span style={{ color: theme.blue }}>✓</span> {item}
                </li>
              ))}
            </ul>
            {isCurrentPlan('pro') ? (
              <button
                disabled
                style={{
                  width: '100%',
                  background: theme.border,
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.textDark,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'not-allowed'
                }}
              >
                Current Plan
              </button>
            ) : isPlanLevel('pro') ? (
              <button
                onClick={handleManageSubscription}
                disabled={loading === 'manage'}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '2px solid ' + theme.blue,
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.blue,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {loading === 'manage' ? 'Loading...' : 'Switch to ' + (billingCycle === 'monthly' ? 'Monthly' : 'Yearly')}
              </button>
            ) : isPlanLevel('proplus') ? (
              <button
                onClick={handleManageSubscription}
                disabled={loading === 'manage'}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '2px solid ' + theme.blue,
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.blue,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {loading === 'manage' ? 'Loading...' : 'Downgrade'}
              </button>
            ) : (
              <button
                onClick={() => handleCheckout('pro')}
                disabled={loading === 'pro'}
                style={{
                  width: '100%',
                  background: theme.blue,
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {loading === 'pro' ? 'Loading...' : 'Subscribe'}
              </button>
            )}
          </div>

          {/* Pro+ */}
          <div style={{
            background: theme.bgCard,
            border: isCurrentPlan('proplus') ? '2px solid ' + theme.purple : '1px solid ' + theme.purple,
            borderRadius: '20px',
            padding: '36px',
            position: 'relative'
          }}>
            {isCurrentPlan('proplus') ? (
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: theme.purple,
                color: '#fff',
                padding: '4px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700
              }}>
                CURRENT
              </div>
            ) : (
              <div style={{
                position: 'absolute',
                top: '-12px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: theme.purple,
                color: '#fff',
                padding: '4px 16px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 700
              }}>
                BEST VALUE
              </div>
            )}
            <h3 style={{ fontFamily: 'Georgia, serif', color: theme.purple, margin: '0 0 8px', fontSize: '28px', fontWeight: 400 }}>Pro+</h3>
            <div style={{ marginBottom: '24px' }}>
              <span style={{ color: theme.textDark, fontSize: '18px', textDecoration: 'line-through', marginRight: '8px' }}>
                ${plans[billingCycle].proplus.originalPrice}
              </span>
              <span style={{ fontSize: '48px', fontWeight: 700, color: theme.text }}>
                ${plans[billingCycle].proplus.price}
              </span>
              <span style={{ color: theme.textMuted, fontSize: '18px' }}>
                /{billingCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
              {billingCycle === 'yearly' && (
                <p style={{ color: theme.green, fontSize: '14px', margin: '8px 0 0' }}>
                  ${plans.yearly.proplus.monthly}/month
                </p>
              )}
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px' }}>
              {['Everything in Pro', 'Portfolio Review (20/day)', 'Upload CSV, PDF, images', 'AI trade timing analysis', 'Personalized tips', 'Historical tracking'].map((item, i) => (
                <li key={i} style={{
                  color: theme.textMuted,
                  fontSize: '16px',
                  padding: '10px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  borderBottom: '1px solid ' + theme.border
                }}>
                  <span style={{ color: theme.purple }}>✓</span> {item}
                </li>
              ))}
            </ul>
            {isCurrentPlan('proplus') ? (
              <button
                disabled
                style={{
                  width: '100%',
                  background: theme.border,
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.textDark,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'not-allowed'
                }}
              >
                Current Plan
              </button>
            ) : isPlanLevel('proplus') ? (
              <button
                onClick={handleManageSubscription}
                disabled={loading === 'manage'}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '2px solid ' + theme.purple,
                  borderRadius: '10px',
                  padding: '16px',
                  color: theme.purple,
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {loading === 'manage' ? 'Loading...' : 'Switch to ' + (billingCycle === 'monthly' ? 'Monthly' : 'Yearly')}
              </button>
            ) : (
              <button
                onClick={() => handleCheckout('proplus')}
                disabled={loading === 'proplus'}
                style={{
                  width: '100%',
                  background: theme.purple,
                  border: 'none',
                  borderRadius: '10px',
                  padding: '16px',
                  color: '#fff',
                  fontSize: '18px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                {loading === 'proplus' ? 'Loading...' : (isPlanLevel('pro') ? 'Upgrade' : 'Subscribe')}
              </button>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Georgia, serif',
            fontSize: '32px',
            fontWeight: 400,
            color: theme.text,
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            Before You Subscribe
          </h2>
          <div style={{
            background: theme.bgCard,
            border: '1px solid ' + theme.border,
            borderRadius: '16px',
            padding: '32px'
          }}>
            {[
              { q: "What's the Launch Special?", a: "50% OFF for early subscribers. Prices shown already reflect the discount. Lock in these prices before they go up!" },
              { q: "How does billing work?", a: "Subscription auto-renews monthly/yearly. You will be charged automatically until you cancel." },
              { q: "Can I cancel anytime?", a: "Yes! Cancel anytime in your account settings. No penalty for cancellation." },
              { q: "What's the refund policy?", a: "Refunds available within 7 days of purchase, prorated by days used." },
              { q: "Can I upgrade or downgrade my plan?", a: "Yes! You can switch between plans anytime. Changes take effect on your next billing cycle." },
              { q: "Is there an age requirement?", a: "You must be 18 years or older to subscribe." },
              { q: "Is this financial advice?", a: "No. InsightStock provides AI-generated analysis for informational purposes only. Always do your own research and consult a qualified financial advisor before making investment decisions. Past performance does not guarantee future results." },
              { q: "Where does the data come from?", a: "Market data provided by Massive.io" }
            ].map((item, i) => (
              <div key={i} style={{
                marginBottom: i < 7 ? '24px' : 0,
                paddingBottom: i < 7 ? '24px' : 0,
                borderBottom: i < 7 ? '1px solid ' + theme.border : 'none'
              }}>
                <p style={{ color: theme.text, fontSize: '18px', fontWeight: 600, margin: '0 0 8px' }}>
                  {i + 1}. {item.q}
                </p>
                <p style={{ color: theme.textMuted, fontSize: '16px', margin: 0, lineHeight: '1.6' }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>

          {/* Footer Links */}
          <div style={{ textAlign: 'center', marginTop: '32px', color: theme.textDark, fontSize: '16px' }}>
            <p style={{ margin: '0 0 12px' }}>
              <a href="#" style={{ color: theme.textMuted, textDecoration: 'underline' }}>Privacy Policy</a>
              {' | '}
              <a href="#" style={{ color: theme.textMuted, textDecoration: 'underline' }}>Terms of Service</a>
            </p>
            <p style={{ margin: 0 }}>Questions? Contact support@insightstock.com</p>
          </div>
        </div>
      </div>
      <AuthModal
        isOpen={authModal.open}
        onClose={() => setAuthModal({ ...authModal, open: false })}
        mode={authModal.mode}
        setMode={(m) => setAuthModal({ ...authModal, mode: m })}
      />
    </div>
  );
}

export default PricingPage;
